import React, { useEffect, useState, useCallback } from 'react';
import { Table, Typography, Tag, Alert, Spin, Button, Select, Space, Modal, Input, notification, Breadcrumb, Checkbox, Card, Popconfirm } from 'antd';
import DatePicker from 'antd/es/date-picker';
import { ArrowLeftOutlined, UploadOutlined, DownloadOutlined, CheckCircleOutlined } from '@ant-design/icons';
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom';
import dayjs from 'dayjs';
import lib from '../../assets/utils/lib';

const { Title, Text } = Typography;

const getServerError = (e, fallback) => (e?.response?.data?.message != null && e.response.data.message !== '') ? e.response.data.message : (e?.message || fallback);
const getServerMessage = (data, fallback) => (data?.message != null && data.message !== '') ? data.message : fallback;

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'under_review', label: 'Under review' },
  { value: 'counter_proposed', label: 'Counter proposed' },
  { value: 'needs_revision', label: 'Needs revision' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'active', label: 'Active' },
  { value: 'draft', label: 'Draft' },
];

const getTariffId = (r) => r?.id ?? r?.tariff_id ?? r?.facility_tariff_id;

/** Normalize active_tariff from GET .../tariffs/active?per_category=1 (nested standard_tariff / facility_tariff). */
const getActiveTariffInfo = (at) => {
  if (!at) return null;
  if (at.source === 'standard') {
    const st = at.standard_tariff;
    return {
      source: 'standard',
      id: at.standard_tariff_id ?? st?.tariff_id ?? st?.id ?? null,
      name: st?.tariff_name ?? st?.name ?? at.tariff_name ?? at.name ?? 'Standard',
    };
  }
  const ft = at.facility_tariff;
  const id = at.facility_tariff_id ?? ft?.facility_tariff_id ?? ft?.tariff_id ?? ft?.id ?? null;
  return {
    source: at.source ?? 'specific',
    id,
    name: ft?.tariff_name ?? ft?.name ?? at.tariff_name ?? at.name ?? (id != null ? `Tariff ${id}` : null),
  };
};

/**
 * Facility tariffs page (admin): list, upload, set active, assign HMO standard, per-category matrix.
 */
const FacilityTariffList = () => {
  const { facilityId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // List
  const [facilityName, setFacilityName] = useState('');
  /** Facility scope (state, zone, etc.) for filtering standards applicable to this facility. From GET facility. */
  const [facilityScope, setFacilityScope] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tariffs, setTariffs] = useState([]);
  const [statusFilter, setStatusFilter] = useState('');
  const [showArchived, setShowArchived] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [total, setTotal] = useState(0);

  // Active tariff (default + per category)
  const [activeTariff, setActiveTariff] = useState(null);
  const [activeByCategory, setActiveByCategory] = useState([]);
  const [activeTariffLoading, setActiveTariffLoading] = useState(false);
  const [matrixCategories, setMatrixCategories] = useState([]);
  const [matrixSavingKey, setMatrixSavingKey] = useState(null);
  const [matrixSelection, setMatrixSelection] = useState({});
  const [approvedTariffs, setApprovedTariffs] = useState([]);
  const [assignStandardLoading, setAssignStandardLoading] = useState(false);
  const [setActiveLoadingId, setSetActiveLoadingId] = useState(null);
  const [activeTariffModalOpen, setActiveTariffModalOpen] = useState(false);
  const [reloadingAfterSave, setReloadingAfterSave] = useState(false);
  /** Full list of standard tariffs from GET .../standards (for dropdown per category). Fetched when modal opens. */
  const [standardTariffsFromApi, setStandardTariffsFromApi] = useState([]);
  /** Backward-compat alias for code that may still reference the old name (e.g. stale hot-reload). */
  const standardTariffFromApi = standardTariffsFromApi?.[0] ?? null;

  // Upload modal
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [uploadCategoryId, setUploadCategoryId] = useState(undefined);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadTariffName, setUploadTariffName] = useState('');
  const [uploadEffectiveDate, setUploadEffectiveDate] = useState(null);
  const [uploadExpiryDate, setUploadExpiryDate] = useState(null);
  const [uploadAutoSubmit, setUploadAutoSubmit] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadValidationErrors, setUploadValidationErrors] = useState([]);
  const [uploadValidationSummary, setUploadValidationSummary] = useState(null);
  const [uploadValidationParsing, setUploadValidationParsing] = useState(null);
  const [uploadCategories, setUploadCategories] = useState([]);

  const fetchActiveTariff = useCallback(async () => {
    if (!facilityId) return;
    setActiveTariffLoading(true);
    try {
      const resp = await lib.getAdminFacilityActiveTariff(facilityId, { per_category: 1 });
      const body = resp?.data;
      if (body?.error) {
        setActiveTariff(null);
        setActiveByCategory([]);
      } else {
        const data = body?.data ?? body;
        const byCat = Array.isArray(data?.active_by_category) ? data.active_by_category : [];
        setActiveByCategory(byCat);
        const topLevel = data?.tariff ?? data;
        const firstActive = byCat[0]?.active_tariff;
        const firstInfo = getActiveTariffInfo(firstActive);
        setActiveTariff(
          topLevel?.tariff_name || topLevel?.name
            ? topLevel
            : firstInfo
              ? { source: firstInfo.source, tariff_name: firstInfo.name, tariff_id: firstInfo.id, facility_tariff_id: firstInfo.source === 'specific' ? firstInfo.id : null }
              : firstActive ?? null
        );
      }
    } catch {
      try {
        const fallback = await lib.getFacilityActiveTariff(facilityId);
        const d = fallback?.data?.data ?? fallback?.data;
        setActiveTariff(d?.tariff ?? d ?? null);
        setActiveByCategory([]);
      } catch {
        setActiveTariff(null);
        setActiveByCategory([]);
      }
    } finally {
      setActiveTariffLoading(false);
    }
  }, [facilityId]);

  useEffect(() => {
    if (facilityId) fetchActiveTariff();
  }, [facilityId, fetchActiveTariff]);

  useEffect(() => {
    if (activeTariffModalOpen && facilityId) fetchActiveTariff();
  }, [activeTariffModalOpen, facilityId, fetchActiveTariff]);

  // When Active tariff modal opens, load standards list (filtered by facility scope per guide §2.5) and refresh approved list
  useEffect(() => {
    if (!activeTariffModalOpen || !facilityId) return;
    const loadStandards = () => {
      const params = facilityScope ? { ...facilityScope } : {};
      if (typeof lib.getAdminStandardTariffs === 'function') {
        return lib.getAdminStandardTariffs(params)
          .then((resp) => {
            const data = resp?.data?.data ?? resp?.data;
            const list = Array.isArray(data?.tariffs) ? data.tariffs : [];
            setStandardTariffsFromApi(list);
          })
          .catch(() => lib.getAdminStandardTariff().then((r) => {
            const d = r?.data?.data ?? r?.data;
            const t = d?.tariff ?? (Array.isArray(d?.tariffs) ? d.tariffs?.[0] : d);
            setStandardTariffsFromApi(t ? [t] : []);
          }).catch(() => setStandardTariffsFromApi([])));
      }
      return lib.getAdminStandardTariff().then((r) => {
        const d = r?.data?.data ?? r?.data;
        const t = d?.tariff ?? (Array.isArray(d?.tariffs) ? d.tariffs?.[0] : d);
        setStandardTariffsFromApi(t ? [t] : []);
      }).catch(() => setStandardTariffsFromApi([]));
    };
    const refreshApproved = () => {
      lib.getAdminFacilityTariffs(facilityId, { limit: 100 }).then((resp) => {
        const data = resp?.data?.data ?? resp?.data;
        const list = Array.isArray(data?.tariffs) ? data.tariffs : Array.isArray(data) ? data : [];
        setApprovedTariffs(list.filter((t) => ['approved', 'active'].includes(String(t?.tariff_status ?? t?.status ?? ''))));
      }).catch(() => setApprovedTariffs([]));
    };
    loadStandards();
    refreshApproved();
  }, [activeTariffModalOpen, facilityId, facilityScope]);

  useEffect(() => {
    if (!facilityId) return;
    const load = (resp) => {
      const body = resp?.data;
      const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
      setMatrixCategories(
        list
          .map((c) => ({ id: c.id ?? c.category_id, name: c.category_name ?? c.name ?? `Category ${c.id ?? c.category_id}` }))
          .filter((c) => c.id != null)
      );
    };
    if (typeof lib.getAdminTariffCategories === 'function') {
      lib.getAdminTariffCategories()
        .then(load)
        .catch(() => lib.getTariffCategories().then(load).catch(() => setMatrixCategories([])));
    } else if (typeof lib.getTariffCategories === 'function') {
      lib.getTariffCategories().then(load).catch(() => setMatrixCategories([]));
    } else {
      setMatrixCategories([]);
    }
  }, [facilityId]);

  useEffect(() => {
    if (!facilityId) return;
    lib.getAdminFacilityTariffs(facilityId, { limit: 100 })
      .then((resp) => {
        const data = resp?.data?.data ?? resp?.data;
        const list = Array.isArray(data?.tariffs) ? data.tariffs : Array.isArray(data) ? data : [];
        setApprovedTariffs(list.filter((t) => ['approved', 'active'].includes(String(t?.tariff_status ?? t?.status ?? ''))));
      })
      .catch(() => setApprovedTariffs([]));
  }, [facilityId]);

  useEffect(() => {
    if (!facilityId) return;
    let cancelled = false;
    lib.getFacilityById(facilityId)
      .then((resp) => {
        if (cancelled) return;
        const f = resp?.data?.data ?? resp?.data;
        setFacilityName(f?.name ?? f?.facility_name ?? 'Facility');
        const scope = {};
        const stateVal = f?.state ?? f?.facility_state;
        if (stateVal != null && stateVal !== '') scope.scope_state = stateVal;
        if (f?.zone_id != null) scope.scope_zone_id = f.zone_id;
        if (f?.facility_type != null && f.facility_type !== '') scope.scope_facility_type = f.facility_type;
        if (f?.plan_id != null) scope.scope_plan_id = f.plan_id;
        if (f?.location_id != null) scope.scope_location_id = f.location_id;
        setFacilityScope(Object.keys(scope).length > 0 ? scope : null);
      })
      .catch(() => { if (!cancelled) setFacilityName('Facility'); setFacilityScope(null); });
    return () => { cancelled = true; };
  }, [facilityId]);

  const fetchTariffs = useCallback(async () => {
    if (!facilityId) return;
    setLoading(true);
    setError(null);
    try {
      const offset = (page - 1) * pageSize;
      const params = { limit: pageSize, offset };
      if (statusFilter) params.status = statusFilter;
      if (showArchived) params.include_archived = true;
      const resp = await lib.getAdminFacilityTariffs(facilityId, params);
      const body = resp?.data;
      if (body?.error) {
        setError((body?.message != null && body.message !== '') ? body.message : 'Unable to load tariffs');
        setTariffs([]);
        setTotal(0);
      } else {
        const data = body?.data ?? body;
        const items = Array.isArray(data?.tariffs) ? data.tariffs : [];
        setTariffs(items);
        setTotal(data?.total ?? items.length);
      }
    } catch (e) {
      setError(getServerError(e, 'Network error while loading tariffs'));
      setTariffs([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [facilityId, statusFilter, showArchived, page, pageSize]);

  useEffect(() => {
    fetchTariffs();
  }, [fetchTariffs]);

  useEffect(() => {
    if (location.state?.openUpload) {
      setUploadModalOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location.state?.openUpload, location.pathname, navigate]);

  useEffect(() => {
    if (uploadModalOpen) {
      setUploadError(null);
      setUploadValidationErrors([]);
      setUploadValidationSummary(null);
      setUploadValidationParsing(null);
      lib.getTariffCategories()
        .then((resp) => {
          const body = resp?.data;
          const list = Array.isArray(body?.data) ? body.data : Array.isArray(body) ? body : [];
          const opts = list
            .map((c) => ({
              value: c.id ?? c.category_id,
              label: c.category_name ?? c.name ?? `Category ${c.id ?? c.category_id}`,
            }))
            .filter((o) => o.value != null);
          setUploadCategories(opts);
        })
        .catch(() => setUploadCategories([]));
    } else {
      setUploadCategoryId(undefined);
      setUploadFile(null);
      setUploadTariffName('');
      setUploadEffectiveDate(null);
      setUploadExpiryDate(null);
      setUploadAutoSubmit(false);
    }
  }, [uploadModalOpen]);

  const handleUploadSubmit = useCallback(async () => {
    if (!uploadTariffName || !uploadEffectiveDate || !uploadFile) {
      setUploadError('Please enter tariff name, effective date, and choose a file.');
      return;
    }
    setUploadLoading(true);
    setUploadError(null);
    setUploadValidationErrors([]);
    setUploadValidationSummary(null);
    setUploadValidationParsing(null);
    try {
      const formData = new FormData();
      formData.append('file', uploadFile);
      formData.append('tariff_name', uploadTariffName);
      formData.append('effective_date', dayjs(uploadEffectiveDate).format('YYYY-MM-DD'));
      if (uploadExpiryDate) formData.append('expiry_date', dayjs(uploadExpiryDate).format('YYYY-MM-DD'));
      formData.append('auto_submit', uploadAutoSubmit ? '1' : '0');
      if (uploadCategoryId != null && uploadCategoryId !== '' && uploadCategoryId !== 'all') {
        formData.append('upload_category_id', String(uploadCategoryId));
      }
      const resp = await lib.uploadTariffForFacility(facilityId, formData);
      const data = resp?.data;
      if (data?.error) {
        setUploadError(getServerMessage(data, 'Upload failed'));
        const payload = data?.data ?? {};
        setUploadValidationErrors(Array.isArray(payload.errors) ? payload.errors : []);
        setUploadValidationSummary(payload.summary ?? null);
        setUploadValidationParsing(payload.parsing ?? null);
        return;
      }
      setUploadModalOpen(false);
      fetchTariffs();
      notification.success({ message: getServerMessage(data, 'Tariff uploaded successfully'), placement: 'topRight' });
    } catch (e) {
      const payload = e?.response?.data?.data ?? {};
      setUploadError(getServerError(e, 'Upload failed'));
      setUploadValidationErrors(Array.isArray(payload.errors) ? payload.errors : []);
      setUploadValidationSummary(payload.summary ?? null);
      setUploadValidationParsing(payload.parsing ?? null);
    } finally {
      setUploadLoading(false);
    }
  }, [facilityId, uploadTariffName, uploadEffectiveDate, uploadExpiryDate, uploadAutoSubmit, uploadFile, uploadCategoryId, fetchTariffs]);

  const handleDownloadTemplate = useCallback((format = 'csv', type = 'minimal') => {
    lib.downloadTariffTemplate(format, type).catch((err) => {
      notification.error({ message: getServerError(err, 'Failed to download template'), placement: 'topRight' });
    });
  }, []);

  const handleSetActive = useCallback((record) => {
    const id = getTariffId(record);
    if (!facilityId || !id) return;
    const categoryId = record.category_id ?? (typeof record.category === 'number' ? record.category : record.category?.id) ?? null;
    if (categoryId != null) {
      // Tariff has a category: set active for that category only (same as modal's per-category save)
      (async () => {
        setSetActiveLoadingId(id);
        try {
          const body = { source: 'specific', facility_tariff_id: Number(id), category_id: Number(categoryId) };
          const setActive = lib.setAdminFacilityActiveTariff || lib.setFacilityActiveTariff;
          const resp = await setActive(facilityId, body);
          if (resp?.data?.error) throw new Error(resp.data.message || 'Failed to set active');
          await fetchActiveTariff();
          await fetchTariffs();
          notification.success({ message: 'Active tariff updated for this category', placement: 'topRight' });
        } catch (e) {
          notification.error({ message: getServerError(e, 'Failed to set active tariff'), placement: 'topRight' });
        } finally {
          setSetActiveLoadingId(null);
        }
      })();
    } else {
      // No category: open the Active tariff modal so user can choose default/category
      setActiveTariffModalOpen(true);
    }
  }, [facilityId, fetchActiveTariff, fetchTariffs]);

  const handleMatrixSave = useCallback(
    async (categoryId, payload) => {
      if (!facilityId) return;
      const key = categoryId == null ? 'default' : categoryId;
      setMatrixSavingKey(key);
      try {
        const body = payload?.source === 'standard'
          ? { source: 'standard', ...(payload?.standard_tariff_id != null && { standard_tariff_id: Number(payload.standard_tariff_id) }) }
          : { source: 'specific', facility_tariff_id: Number(payload?.facility_tariff_id ?? payload) };
        if (categoryId != null) body.category_id = Number(categoryId);
        const setActive = lib.setAdminFacilityActiveTariff || lib.setFacilityActiveTariff;
        const resp = await setActive(facilityId, body);
        if (resp?.data?.error) throw new Error(resp.data.message || 'Failed to set active');
        setMatrixSelection((s) => ({ ...s, [key]: undefined }));
        setReloadingAfterSave(true);
        try {
          await fetchActiveTariff();
          await fetchTariffs();
        } finally {
          setReloadingAfterSave(false);
        }
        notification.success({ message: 'Active tariff updated', placement: 'topRight' });
      } catch (e) {
        notification.error({ message: getServerError(e, 'Failed to update active tariff'), placement: 'topRight' });
      } finally {
        setMatrixSavingKey(null);
      }
    },
    [facilityId, fetchActiveTariff, fetchTariffs]
  );

  // Remove a request category's assignment entirely (not replace it). The
  // category then stops being offered for encounters because of it.
  const [matrixRemovingKey, setMatrixRemovingKey] = useState(null);
  const handleMatrixRemove = useCallback(
    async (categoryId, categoryName) => {
      if (!facilityId || categoryId == null) return;
      setMatrixRemovingKey(categoryId);
      try {
        const resp = await lib.removeAdminFacilityCategoryTariff(facilityId, categoryId);
        // myApi already showed the server's error message.
        if (resp?.error) return;
        if (resp?.data?.error) throw new Error(resp.data.message || 'Failed to remove the assignment');
        setMatrixSelection((s) => ({ ...s, [categoryId]: undefined }));
        setReloadingAfterSave(true);
        try {
          await fetchActiveTariff();
          await fetchTariffs();
        } finally {
          setReloadingAfterSave(false);
        }
        const branches = resp?.data?.data?.branches || [];
        notification.success({
          message: `Tariff assignment removed from ${categoryName || 'this category'}`,
          description: branches.length
            ? `${branches.length} linked branch${branches.length === 1 ? '' : 'es'} also lost this assignment.`
            : undefined,
          placement: 'topRight',
        });
      } catch (e) {
        notification.error({ message: getServerError(e, 'Failed to remove the tariff assignment'), placement: 'topRight' });
      } finally {
        setMatrixRemovingKey(null);
      }
    },
    [facilityId, fetchActiveTariff, fetchTariffs]
  );

  const handleAssignStandard = useCallback(async () => {
    if (!facilityId) return;
    setAssignStandardLoading(true);
    try {
      const setActive = lib.setAdminFacilityActiveTariff || lib.setFacilityActiveTariff;
      const resp = await setActive(facilityId, { source: 'standard' });
      if (resp?.data?.error) throw new Error(resp.data.message || 'Failed to set active');
      setReloadingAfterSave(true);
      try {
        await fetchActiveTariff();
        await fetchTariffs();
      } finally {
        setReloadingAfterSave(false);
      }
      setMatrixSelection({});
      notification.success({ message: 'HMO standard assigned for this facility', placement: 'topRight' });
    } catch (e) {
      notification.error({ message: getServerError(e, 'Failed to assign HMO standard'), placement: 'topRight' });
    } finally {
      setAssignStandardLoading(false);
    }
  }, [facilityId, fetchActiveTariff, fetchTariffs]);

  const activeTariffId = activeTariff?.facility_tariff_id ?? activeTariff?.id ?? activeTariff?.tariff_id;

  const columns = [
    { title: 'Tariff Name', dataIndex: 'tariff_name', key: 'tariff_name', ellipsis: true, render: (t, r) => t ?? r?.name ?? '—' },
    {
      title: 'Category',
      key: 'category',
      width: 120,
      render: (_, r) => r?.category_name ?? r?.category ?? (r?.category_id != null ? `Category ${r.category_id}` : '—'),
    },
    {
      title: 'Status',
      dataIndex: 'tariff_status',
      key: 'tariff_status',
      width: 150,
      render: (status, r) => {
        // tariff_archived is orthogonal to tariff_status — an approved/active
        // tariff can be archived, so this renders alongside the status tag
        // rather than replacing it.
        const s = status ?? r?.status;
        const archived = Boolean(r?.tariff_archived) || r?.tariff_archived === 1 || r?.tariff_archived === '1';
        if (!s) return <>{<Tag>unknown</Tag>}{archived ? <Tag color="default">Archived</Tag> : null}</>;
        let color = 'default';
        if (s === 'pending_review' || s === 'submitted') color = 'gold';
        else if (s === 'under_review') color = 'blue';
        else if (s === 'approved' || s === 'active') color = 'green';
        else if (s === 'rejected') color = 'red';
        else if (s === 'counter_proposed' || s === 'needs_revision') color = 'purple';
        return (
          <>
            <Tag color={color}>{String(s)}</Tag>
            {archived ? <Tag color="default">Archived</Tag> : null}
          </>
        );
      },
    },
    {
      title: 'Effective date',
      dataIndex: 'effective_date',
      key: 'effective_date',
      width: 120,
      render: (v, r) => (v || r?.effectiveDate ? new Date(v || r.effectiveDate).toLocaleDateString() : '—'),
    },
    {
      title: 'Items',
      dataIndex: 'total_items',
      key: 'total_items',
      width: 80,
      align: 'right',
      // render: (v, r) => v ?? r?.items?.length ?? '—',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 140,
      render: (_, record) => {
        const id = getTariffId(record);
        if (!id) return '—';
        return (
          <Space size="small" onClick={(e) => e.stopPropagation()}>
            <Button
              type="link"
              size="small"
              onClick={() => handleSetActive(record)}
            >
              Set active
            </Button>
          </Space>
        );
      },
    },
    // {
    //   title: 'Actions',
    //   key: 'actions',
    //   width: 180,
    //   render: (_, record) => {
    //     const id = getTariffId(record);
    //     const isActive = id != null && String(id) === String(activeTariffId);
    //     return id ? (
    //       <Space size="small" onClick={(e) => e.stopPropagation()}>
    //         <Button
    //           type="link"
    //           size="small"
    //           onClick={() => navigate(`/facilities/${facilityId}/tariffs/${encodeURIComponent(id)}`, { state: { tariff: record } })}
    //         >
    //           Open
    //         </Button>
    //         {!isActive && (
    //           <Button
    //             type="link"
    //             size="small"
    //             loading={setActiveLoadingId === id}
    //             onClick={() => handleSetActive(record)}
    //           >
    //             Set active
    //           </Button>
    //         )}
    //         {isActive && <Tag color="green" icon={<CheckCircleOutlined />}>Active</Tag>}
    //       </Space>
    //     ) : '—';
    //   },
    // },
  ];

  const featureEnabled =
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === '1' ||
    process.env.REACT_APP_TARIFF_FEATURE_ENABLED === 'true';

  if (!facilityId) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="error" message="Missing facility" description="No facility ID in URL." />
        <Button type="link" onClick={() => navigate('/facilities')}>Back to Facilities</Button>
      </div>
    );
  }

  if (!featureEnabled) {
    return (
      <div style={{ padding: 24 }}>
        <Alert type="info" message="Tariff module is currently disabled" />
        <Button type="link" onClick={() => navigate(`/facilities/${facilityId}`)}>Back to Facility</Button>
      </div>
    );
  }

  const matrixRows = [
    // { key: 'default', categoryId: null, categoryName: 'Default' },
    ...matrixCategories.map((c) => ({ key: `cat-${c.id}`, categoryId: c.id, categoryName: c.name })),
  ];

  // Standards for a row: (1) standards with no category (generic) → included in every row; (2) standards whose category matches this row → only on that category row.
  const getStandardsForRow = (rowCategoryId) => {
    return standardTariffsFromApi.filter((s) => {
      const sid = s.category_id ?? s.category;
      const noCategory = sid == null || sid === undefined || sid === '';
      if (rowCategoryId == null) return noCategory; // Default row: only standards with no category (generic)
      return noCategory || sid == rowCategoryId; // Category row: generic (no category) + matching category
    });
  };
  // Build dropdown options for standards: one option per standard in the filtered list for this row.
  const getStandardOptionsForRow = (rowCategoryId) => {
    return getStandardsForRow(rowCategoryId).map((s) => {
      const id = s.tariff_id ?? s.id;
      return {
        value: id != null ? `standard:${id}` : null,
        label: s.tariff_name ?? s.name ?? s.category_name ?? `Standard ${id ?? ''}`,
      };
    }).filter((o) => o.value != null);
  };

  const showMatrix = matrixCategories.length > 0 || activeByCategory.length > 0;

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate(`/facilities/${facilityId}`)}>
          Back to Facility
        </Button>
        <Breadcrumb
          items={[
            { title: <Link to="/facilities">Facilities</Link> },
            { title: <Link to={`/facilities/${facilityId}`}>{facilityName || 'Facility'}</Link> },
            { title: 'Tariffs' },
          ]}
        />
      </div>

      <Title level={4} style={{ marginBottom: 4 }}>Facility tariffs</Title>
      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        {facilityName || 'Facility'} — upload tariffs, set active, or assign HMO standard.
      </Text>

      {error && (
        <Alert type="error" showIcon style={{ marginBottom: 16 }} message="Error" description={error} />
      )}

      <Space wrap size="middle" style={{ marginBottom: 16 }}>
        <Select
          placeholder="Status"
          value={statusFilter || undefined}
          onChange={(v) => { setStatusFilter(v ?? ''); setPage(1); }}
          options={STATUS_OPTIONS}
          style={{ minWidth: 140 }}
          allowClear
        />
        <Checkbox
          checked={showArchived}
          onChange={(e) => { setShowArchived(e.target.checked); setPage(1); }}
        >
          Show archived
        </Checkbox>
        <Button onClick={fetchTariffs} loading={loading}>Refresh</Button>
        <Button onClick={() => setActiveTariffModalOpen(true)}>
          {activeTariffLoading ? 'Active: …' : activeTariff
            ? `Active: ${activeTariff?.tariff_name ?? activeTariff?.name ?? 'Tariff'}`
            : 'Active tariff'}
        </Button>
        <Button type="primary" icon={<UploadOutlined />} onClick={() => setUploadModalOpen(true)}>
          Upload tariff
        </Button>
      </Space>

      <Modal
        title="Active tariffs"
        open={activeTariffModalOpen}
        onCancel={() => setActiveTariffModalOpen(false)}
        footer={[<Button key="close" onClick={() => setActiveTariffModalOpen(false)}>Close</Button>]}
        // 640 was narrower than the matrix table actually is (category +
        // current tariff name + select + Save ≈ 900px), and the overflow
        // painted row borders and cells past the panel onto the backdrop.
        // Wide enough for the real content, capped to the viewport, and the
        // table below scrolls internally as the backstop.
        width={showMatrix ? 920 : 420}
        style={{ maxWidth: '95vw' }}
        destroyOnClose
      >
        <Spin spinning={reloadingAfterSave} tip="Refreshing…">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {/* <div>
            <Text type="secondary">Current active: </Text>
            {activeTariffLoading ? (
              <Text type="secondary">Loading…</Text>
            ) : activeTariff ? (
              <Tag color="green" icon={<CheckCircleOutlined />}>
                {activeTariff?.tariff_name ?? activeTariff?.name ?? `Tariff ${activeTariff?.facility_tariff_id ?? activeTariff?.id ?? ''}`}
                {activeTariff?.source ? ` (${activeTariff.source})` : ''}
              </Tag>
            ) : (
              <Text type="secondary">None</Text>
            )}
          </div>
          {(() => {
            const firstStandard = standardTariffsFromApi[0];
            if (!firstStandard) return null;
            return (
              <Button loading={assignStandardLoading} onClick={handleAssignStandard}>
                Assign {firstStandard?.tariff_name ?? firstStandard?.name ?? 'standard'}
              </Button>
            );
          })()} */}
          {showMatrix && (
            <>
              {/* <Text strong>By category</Text> */}
              <Table
                size="small"
                dataSource={matrixRows}
                pagination={false}
                // Contain, whatever the columns grow to: anything wider than
                // the modal scrolls inside it instead of bleeding out of it.
                scroll={{ x: 'max-content' }}
                columns={[
                  {
                    title: 'Category',
                    dataIndex: 'categoryName',
                    key: 'categoryName',
                    width: 180,
                  },
                  {
                    title: 'Current active',
                    key: 'current',
                    render: (_, row) => {
                      const entry = activeByCategory.find((e) => (e.category_id == null && row.categoryId == null) || e.category_id == row.categoryId);
                      const info = getActiveTariffInfo(entry?.active_tariff);
                      if (!info) return <Text type="secondary">None</Text>;
                      if (info.source === 'standard') return <Tag>{info.name}</Tag>;
                      return <Text>{info.name}</Text>;
                    },
                  },
                  {
                    title: 'Change to',
                    key: 'change',
                    render: (_, row) => {
                      const key = row.categoryId == null ? 'default' : row.categoryId;
                      const entry = activeByCategory.find((e) => (e.category_id == null && row.categoryId == null) || e.category_id == row.categoryId);
                      const info = getActiveTariffInfo(entry?.active_tariff);
                      const standardOptionsForRow = getStandardOptionsForRow(row.categoryId);
                      const currentValue = info?.source === 'standard' && info.id != null
                        ? `standard:${info.id}`
                        : (info?.source === 'specific' && info.id != null ? info.id : undefined);
                      const selected = matrixSelection[key] !== undefined ? matrixSelection[key] : currentValue;
                      const options = [
                        ...standardOptionsForRow,
                        ...approvedTariffs.map((t) => ({ value: getTariffId(t), label: t?.tariff_name ?? t?.name ?? `Tariff ${getTariffId(t)}` })),
                      ].filter((o) => o.value != null && o.value !== '');
                      const saving = matrixSavingKey === key;
                      return (
                        <Space size="small">
                          <Select
                            value={selected != null ? selected : undefined}
                            options={options}
                            onChange={(val) => setMatrixSelection((s) => ({ ...s, [key]: val }))}
                            // Bounded, not minWidth: long tariff names were
                            // free to stretch the select (and the row) to
                            // whatever they measured; now they ellipsize.
                            style={{ width: 260 }}
                            placeholder="Select…"
                            allowClear={false}
                          />
                          <Button
                            type="primary"
                            size="small"
                            loading={saving}
                            onClick={() => {
                              const val = matrixSelection[key] !== undefined ? matrixSelection[key] : currentValue;
                              if (val == null) return;
                              const isStandard = typeof val === 'string' && val.startsWith('standard:');
                              const payload = isStandard
                                ? { source: 'standard', standard_tariff_id: Number(val.replace(/^standard:/, '')) }
                                : { source: 'specific', facility_tariff_id: Number(val) };
                              handleMatrixSave(row.categoryId, payload);
                            }}
                          >
                            Save
                          </Button>
                          {row.categoryId != null && info ? (
                            <Popconfirm
                              title={`Remove the tariff from ${row.categoryName || 'this category'}?`}
                              description="The category will no longer be offered for encounters at this facility because of this assignment. Linked branches follow."
                              okText="Remove"
                              okButtonProps={{ danger: true }}
                              onConfirm={() => handleMatrixRemove(row.categoryId, row.categoryName)}
                            >
                              <Button size="small" danger loading={matrixRemovingKey === row.categoryId}>
                                Remove
                              </Button>
                            </Popconfirm>
                          ) : null}
                        </Space>
                      );
                    },
                  },
                ]}
              />
            </>
          )}
        </Space>
        </Spin>
      </Modal>

      <Card size="small" title="Tariffs">
        <Spin spinning={loading}>
          <Table
          size="small"
          rowKey={(r) => r.id ?? r.tariff_id ?? r.facility_tariff_id ?? r.key}
          dataSource={tariffs}
          columns={columns}
          onRow={(record) => {
            const id = getTariffId(record);
            return {
              onClick: () => id && navigate(`/facilities/${facilityId}/tariffs/${encodeURIComponent(id)}`, { state: { tariff: record } }),
              style: id ? { cursor: 'pointer' } : undefined,
            };
          }}
          pagination={{
            current: page,
            pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50'],
            showTotal: (t) => `Total ${t} tariff(s)`,
            onChange: (p, size) => {
              setPage(p);
              setPageSize(size || 20);
            },
          }}
          locale={{ emptyText: loading ? 'Loading…' : 'No tariffs for this facility' }}
        />
        </Spin>
      </Card>

      <Modal
        title="Upload tariff for this facility"
        open={uploadModalOpen}
        onCancel={() => setUploadModalOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setUploadModalOpen(false)}>Cancel</Button>,
          <Button
            key="submit"
            type="primary"
            loading={uploadLoading}
            onClick={handleUploadSubmit}
            disabled={!uploadTariffName || !uploadEffectiveDate || !uploadFile}
          >
            Upload
          </Button>,
        ]}
        width={520}
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
            <Text type="secondary">Upload a CSV/TSV/Excel file. Optionally choose one category so the file can omit the category column.</Text>
            <div>
              <Text strong>Category (optional)</Text>
              <Select
                placeholder="All categories (file must include category per row)"
                allowClear
                value={uploadCategoryId}
                onChange={setUploadCategoryId}
                options={[{ value: 'all', label: 'All categories' }, ...uploadCategories]}
                style={{ width: '100%', marginTop: 4 }}
              />
            </div>
            <div>
              <Text strong>File (CSV, TSV, or Excel)</Text>
              <Input type="file" accept=".csv,.tsv,.xlsx,.xls" style={{ marginTop: 4 }} onChange={(e) => setUploadFile(e.target?.files?.[0] ?? null)} />
            </div>
            <div>
              <Text strong>Tariff name</Text>
              <Input placeholder="e.g. 2024 Drug Tariff" value={uploadTariffName} onChange={(e) => setUploadTariffName(e.target.value)} style={{ marginTop: 4 }} />
            </div>
            <div>
              <Text strong>Effective date</Text>
              <DatePicker value={uploadEffectiveDate} onChange={setUploadEffectiveDate} style={{ width: '100%', marginTop: 4 }} format="YYYY-MM-DD" />
            </div>
            <div>
              <Text strong>Expiry date (optional)</Text>
              <DatePicker value={uploadExpiryDate} onChange={setUploadExpiryDate} style={{ width: '100%', marginTop: 4 }} format="YYYY-MM-DD" />
            </div>
            <Checkbox checked={uploadAutoSubmit} onChange={(e) => setUploadAutoSubmit(e.target.checked)}>
              Auto-submit tariff after upload
            </Checkbox>
            <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('csv', 'minimal')} style={{ padding: 0 }}>Download minimal template (CSV)</Button>
            <Button type="link" icon={<DownloadOutlined />} onClick={() => handleDownloadTemplate('csv', 'advanced')} style={{ padding: 0 }}>Download advanced template (CSV)</Button>
            {uploadError && <Alert type="error" showIcon message={uploadError} style={{ marginTop: 8 }} />}
            {(uploadValidationSummary || uploadValidationParsing || uploadValidationErrors.length > 0) && (
              <div style={{ marginTop: 8 }}>
                {uploadValidationSummary && <Text strong style={{ display: 'block', marginBottom: 4 }}>{uploadValidationSummary}</Text>}
                {uploadValidationParsing && (
                  <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>
                    Rows: {uploadValidationParsing.total_rows ?? '—'} total, {uploadValidationParsing.valid_rows ?? 0} valid, {uploadValidationParsing.invalid_rows ?? 0} invalid
                  </Text>
                )}
            {uploadValidationErrors.length > 0 && (
              <Table
                size="small"
                dataSource={uploadValidationErrors}
                rowKey={(r, i) => (r.row != null ? `row-${r.row}` : `err-${i}`)}
                columns={[
                  { title: 'Row', dataIndex: 'row', key: 'row', width: 56 },
                  { title: 'Code', dataIndex: 'error_code', key: 'error_code', width: 100, ellipsis: true },
                  { title: 'Field', dataIndex: 'field', key: 'field', width: 90 },
                  { title: 'Row values', key: 'row_values', render: (_, r) => (Array.isArray(r.row_values) ? r.row_values.join(', ') : r.row_values ?? '—') },
                  { title: 'Error', dataIndex: 'message', key: 'message', ellipsis: true },
                  { title: 'Suggestion', dataIndex: 'suggestion', key: 'suggestion', ellipsis: true },
                ]}
                pagination={false}
                scroll={{ x: 520 }}
                style={{ marginTop: 4 }}
              />
            )}
          </div>
        )}
      </Space>
      </Modal>
    </div>
  );
};

export default FacilityTariffList;
